import '../../styles/restScreens.css';
import { useMemo, useState } from 'react';
import { PEACOCK_ACCENTS, DEFAULT_ACCENT, isPaletteAccent } from '../../config/peacockPalette';
import { deriveScale } from '../../config/brandPalette';

// Settings › Organization › BRANDING.
//
// WHY THIS EXISTS (Val, 2026-07-17): the old brand picker was a row of 8px nameless
// circles with no preview and no way to tell "not set" from "the default" — because the
// default IS the first swatch (Peacock Teal #2DD4BF), so an unset entity, an entity set to
// teal, and the placeholder all rendered identical pixels. You clicked a swatch and had no
// idea what it did or whether it was already on.
//
// The fix Val asked for, in two halves:
//   1. The list is a READOUT. It shows each compartment's colour and says, in plain words,
//      where that colour comes from — set here, inherited, or the default. No stray click can
//      change the company's colour from this list.
//   2. Editing opens a panel that SHOWS THE REAL THING: a button, a link and an active menu
//      item painted in the colour under the cursor, so you see the result before you commit.
//
// The preview paints with deriveScale() + inline styles — the same ramp applyBrand() writes —
// so it is faithful to the running app whether or not the accent-role repaint (PR #49) has
// landed. It never depends on --btn-fill/--accent-ink existing on this branch.

// One rung of a derived ramp as a CSS rgb() string. deriveScale returns "R G B" channels.
const rung = (scale, k) => (scale ? `rgb(${scale[k]})` : 'transparent');

// WCAG relative-contrast between an "R G B" channel string and a #hex.
const relLum = (r, g, b) => {
    [r, g, b] = [r, g, b].map((v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (channels, hex) => {
    if (!channels) return 0;
    const [r, g, b] = channels.split(' ').map(Number);
    const h = hex.replace('#', '');
    const [R, G, B] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    const [l1, l2] = [relLum(r, g, b), relLum(R, G, B)].sort((a, z) => z - a);
    return (l1 + 0.05) / (l2 + 0.05);
};

// THE DARK-BRAND ANSWER (Val, 2026-07-17: "what colour is dark enough to override Black?").
// Nothing overrides the canvas — canvas is platform-fixed; the brand is an ACCENT on it. But a
// dark brand (The Ride's black: #000 at rung 500 is 1.09:1 on the #0A0E14 canvas — invisible)
// can't be seen as a dark accent on near-black. So we don't pin a rung: we AUTO-LIFT, walking
// the brand's OWN ramp to the lightest rung that clears 4.5:1 on dark (or darkest on light).
// Black becomes a legible pale-grey accent on dark and stays true black on light — automatic,
// per-brand, no human tuning. Returns a rgb() string ready for a style.
function legibleAccent(scale, canvasHex, dark) {
    if (!scale) return 'transparent';
    const order = dark ? [500, 400, 300, 200, 100, 50] : [800, 700, 900, 600, 500];
    const pick = order.find((k) => contrast(scale[k], canvasHex) >= 4.5);
    // If even the extreme rung can't clear it (a mid-grey against mid-grey never happens with our
    // near-black/white canvases), fall back to the lightest/darkest end so it's never invisible.
    return rung(scale, pick ?? (dark ? 50 : 900));
}

// The colour a compartment ACTUALLY wears, and the plain-English reason. Walks up the parent
// chain the way inheritance does, so the readout matches what the app would render.
function resolveBrand(entity, byId) {
    const own = entity.color_palette?.primary;
    if (own) {
        const named = PEACOCK_ACCENTS.find((c) => c.hex.toLowerCase() === own.toLowerCase());
        return { hex: own, source: 'own', label: named ? named.name : own.toUpperCase() };
    }
    let p = entity.parent_entity_id ? byId[entity.parent_entity_id] : null;
    while (p) {
        if (p.color_palette?.primary) {
            return { hex: p.color_palette.primary, source: 'inherited', from: p.name };
        }
        p = p.parent_entity_id ? byId[p.parent_entity_id] : null;
    }
    return { hex: DEFAULT_ACCENT, source: 'default' };
}

// The real components, painted from a hex, on a canvas the viewer can flip between light and
// dark — so a brand can be checked against BOTH skins before saving (Val, 2026-07-17: "Org /
// Brand / Entity should preview this against their colorization"). Every accent here goes
// through legibleAccent(), so the sample is honest even for a near-black brand: it shows the
// auto-lifted colour the live app should use, not an invisible one.
function BrandPreview({ hex }) {
    const [skin, setSkin] = useState('dark');   // default dark — that's where a dark brand fails
    const dark = skin === 'dark';
    const scale = useMemo(() => deriveScale(hex), [hex]);
    const canvas = dark ? '#0A0E14' : '#FFFFFF';
    const ink = dark ? '#F1F5F9' : '#1F2937';

    const accent = legibleAccent(scale, canvas, dark);   // legible on THIS canvas, whatever the brand
    const btnInk = dark ? '#0A0E14' : '#FFFFFF';          // dark ink on the lifted accent / white on the deep one
    const navBg = dark ? `rgb(${scale?.[900] || '18 85 76'} / 0.35)` : rung(scale, 50);

    const lifted = dark && scale && contrast(scale[500], canvas) < 4.5;

    return (
        <div className="rounded-lg border overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 text-xs" style={{ background: dark ? '#111827' : '#F3F4F6', color: dark ? '#94A3B8' : '#6B7280' }}>
                <span className="font-semibold uppercase tracking-wide">Here's what you're changing</span>
                <span className="inline-flex rounded overflow-hidden border" style={{ borderColor: dark ? '#374151' : '#D1D5DB' }}>
                    <button type="button" onClick={() => setSkin('light')} className="px-2 py-0.5" style={{ background: !dark ? '#FFFFFF' : 'transparent', color: !dark ? '#1F2937' : 'inherit' }}>Light</button>
                    <button type="button" onClick={() => setSkin('dark')} className="px-2 py-0.5" style={{ background: dark ? '#0A0E14' : 'transparent', color: dark ? '#F1F5F9' : 'inherit' }}>Dark</button>
                </span>
            </div>
            <div className="p-4" style={{ background: canvas }}>
                {/* active menu item */}
                <div className="flex items-center gap-3 px-3 py-2 rounded" style={{ background: navBg, borderLeft: `4px solid ${accent}`, color: accent }}>
                    <span>📊</span><span className="font-medium">Dashboard</span>
                    <span className="ml-auto text-[10px] opacity-70">← active menu item</span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2 mt-1" style={{ color: ink }}>
                    <span>👥</span><span className="font-medium">CRM</span>
                </div>
                {/* button + link */}
                <div className="flex items-center gap-4 mt-4">
                    <button type="button" className="px-4 py-2 rounded font-medium text-sm" style={{ background: accent, color: btnInk }}>
                        A button
                    </button>
                    <span className="text-sm font-medium underline" style={{ color: accent }}>A link looks like this</span>
                </div>
                {lifted && (
                    <div className="mt-3 text-[11px]" style={{ color: '#94A3B8' }}>
                        ↑ This brand is too dark to read on the dark canvas, so it's shown lightened just enough to stay legible. On light, it keeps its true colour.
                    </div>
                )}
            </div>
        </div>
    );
}

// The edit experience for ONE compartment. Cards you can see the result of, then Save.
function EditBranding({ entity, dark, onClose, onSave }) {
    const initial = entity.color_palette?.primary || '';
    const [picked, setPicked] = useState(initial);   // '' = clear / inherit / default
    const [hover, setHover] = useState(null);
    const [hexOpen, setHexOpen] = useState(initial && !isPaletteAccent(initial));
    const [hexText, setHexText] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // What the preview shows: the swatch under the cursor if any, else the current pick, else
    // the default — so hovering is a try-before-you-buy and the pane is never blank.
    const previewHex = hover || picked || DEFAULT_ACCENT;

    const toHex = (v) => {
        if (!v) return null;
        const s = String(v).trim().replace(/^#+/, '');
        if (/^[0-9a-fA-F]{6}$/.test(s)) return '#' + s.toLowerCase();
        if (/^[0-9a-fA-F]{3}$/.test(s)) return '#' + s.split('').map((c) => c + c).join('').toLowerCase();
        return null;
    };

    const save = async () => {
        setError(null);
        let value = picked;
        if (hexOpen && hexText) {
            const h = toHex(hexText);
            if (!h) { setError('That is not a valid colour. Use a hex like #EC4899.'); return; }
            value = h;
        }
        try {
            setSaving(true);
            await onSave(entity, value || null);   // '' -> null: clear, fall back to inherit/default
            onClose();
        } catch (e) {
            setError(e?.message || 'Could not save.');
            setSaving(false);
        }
    };

    const cardBase = dark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${dark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-xl font-bold ${'text-[var(--ink)]'}`}>🎨 Branding — {entity.name}</h2>
                    <button onClick={onClose} className={`text-2xl ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>×</button>
                </div>

                <div className="p-6 space-y-5">
                    <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                        This colour paints <strong>buttons, links and the active menu item</strong>. Everyone who logs
                        into <strong>{entity.name}</strong> sees it. It does <strong>not</strong> change your light/dark
                        setting — that one is yours alone, in Display &amp; Theme.
                    </p>

                    {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                    {/* the swatch cards — big, named, with an Active badge */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {PEACOCK_ACCENTS.map((c) => {
                            const selected = !hexOpen && (picked || '').toLowerCase() === c.hex.toLowerCase();
                            return (
                                <button
                                    key={c.hex}
                                    type="button"
                                    onMouseEnter={() => setHover(c.hex)}
                                    onMouseLeave={() => setHover(null)}
                                    onClick={() => { setPicked(c.hex); setHexOpen(false); }}
                                    className={`p-3 rounded-lg border-2 transition-all text-center ${selected ? 'border-brand-500 ring-2 ring-offset-2 ring-brand-400' : `${cardBase} hover:scale-[1.03]`}`}
                                >
                                    <div className="w-full h-10 rounded mb-2" style={{ backgroundColor: c.hex }} />
                                    <div className={`text-xs font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{c.name}</div>
                                    {c.note && <div className={`text-[10px] ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{c.note}</div>}
                                    {selected && <div className="mt-1 text-[10px] font-semibold text-brand-600">✓ Active</div>}
                                </button>
                            );
                        })}
                    </div>

                    {/* live preview of the real thing */}
                    <BrandPreview hex={previewHex} />

                    {/* clear + exact hex, tucked under */}
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <button
                            type="button"
                            onClick={() => { setPicked(''); setHexOpen(false); setHexText(''); }}
                            className={`px-3 py-1.5 rounded border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                            Clear — inherit / use the default
                        </button>
                        <button
                            type="button"
                            onClick={() => setHexOpen((v) => !v)}
                            className={`px-3 py-1.5 rounded border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                            Need an exact brand colour? {hexOpen ? '▴' : '▾'}
                        </button>
                        {hexOpen && (
                            <span className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={toHex(hexText) || DEFAULT_ACCENT}
                                    onChange={(e) => { setHexText(e.target.value); setHover(e.target.value); }}
                                    className="w-10 h-9 rounded border cursor-pointer bg-transparent"
                                />
                                <input
                                    type="text"
                                    value={hexText}
                                    onChange={(e) => setHexText(e.target.value)}
                                    onBlur={() => { const h = toHex(hexText); if (h) { setHexText(h); setHover(null); } }}
                                    placeholder={DEFAULT_ACCENT}
                                    className={`font-mono w-32 px-2 py-1.5 rounded border ${'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'}`}
                                />
                            </span>
                        )}
                    </div>
                    {hexOpen && hexText && toHex(hexText) && !isPaletteAccent(hexText) && (
                        <div className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Off-palette colour — it will work, but it won't sit with the signature gradient the way the palette does.
                        </div>
                    )}
                </div>

                <div className={`sticky bottom-0 border-t px-6 py-4 flex justify-end gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <button onClick={onClose} disabled={saving} className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}>Cancel</button>
                    <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save branding'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// The read-only readout + the Edit door.
function BrandingPanel({ entities = [], theme = 'light', onSaveColor }) {
    const dark = theme === 'dark';
    const [editing, setEditing] = useState(null);
    const byId = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);

    const rows = entities.filter((e) => e.status !== 'archived');

    return (
        <div data-m6-layout="entities" data-m6-component="BrandingPanel">
            <p className={`text-sm mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                The colour each Company/Market wears. This is the company's identity — everyone who logs in sees it —
                so it lives in the database, not your browser. To change one, click <strong>Edit</strong>; you'll see
                the result on a real button and menu before you save.
            </p>

            <div className="space-y-2">
                {rows.map((e) => {
                    const b = resolveBrand(e, byId);
                    const scale = deriveScale(b.hex);
                    const status =
                        b.source === 'own' ? `Brand colour: ${b.label}`
                        : b.source === 'inherited' ? `Inheriting from ${b.from}`
                        : 'Not set — using the default (Peacock Teal)';
                    return (
                        <div key={e.id} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className="w-6 h-6 rounded-full border flex-shrink-0" style={{ background: rung(scale, 500), borderColor: dark ? '#374151' : '#D1D5DB' }} />
                            <div className="flex-1 min-w-0">
                                <div className={`font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{e.name}</div>
                                <div className={`text-xs ${b.source === 'default' ? (dark ? 'text-gray-500' : 'text-gray-400') : (dark ? 'text-gray-400' : 'text-gray-500')}`}>
                                    {status}
                                </div>
                            </div>
                            <button
                                onClick={() => setEditing(e)}
                                className={`px-3 py-1.5 rounded text-sm font-medium border ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                                Edit
                            </button>
                        </div>
                    );
                })}
                {rows.length === 0 && (
                    <div className={`text-sm p-4 text-center ${dark ? 'text-gray-500' : 'text-gray-400'}`}>No Companies/Markets yet — add one under Structure first.</div>
                )}
            </div>

            {editing && (
                <EditBranding
                    entity={editing}
                    dark={dark}
                    onClose={() => setEditing(null)}
                    onSave={onSaveColor}
                />
            )}
        </div>
    );
}

export default BrandingPanel;
